import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PlanChangedModal } from "@features/PlanChangedModal/PlanChangedModal";
import { Button } from "@components/Button";
import { DueDayPicker } from "@components/DueDayPicker/DueDayPicker";
import { FeatureFlag } from "@components/FeatureFlag/FeatureFlag";
import { PlusIcon, ReceiptIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Label } from "@components/Label/Label";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import useDebounce from "@hooks/useDebounce";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useModal } from "@hooks/useModal";
import { useModalActions, type IUseModalProps } from "@store/modal/modal";
import { type IOverviewJointBillService } from "@services/overview/types";
import { useAddServiceProviderMutation } from "@services/serviceProviders/mutations";
import {
  useServiceProviderPaymentInstructionCodesQuery,
  useServiceProviderRegistrationInfoQuery,
} from "@services/serviceProviders/queries";
import { type IServiceProviderRegistrationInfo } from "@services/serviceProviders/types";
import { getAxiosErrorData, isNullOrUndefined } from "@utils/common";
import { COUNTRY_CODE } from "@/constants";
import { CountryCode } from "@/i18n";
import {
  useAddBillSettingsModalSchema,
  type TAddBillSettingsModalSchema,
} from "./useAddBillSettingsModalSchema";

interface IAddBilLSettingsModalProps extends IUseModalProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  serviceProviderId: IServiceProviderRegistrationInfo["serviceProviderId"];
}

export const AddBillSettingsModal: React.FC<IAddBilLSettingsModalProps> = (
  props,
) => {
  const { serviceProviderId, jointBillServiceId, ...rest } = props;
  const { isBillVariantSelectorEnabled } = useFeatureConfig();
  const modal = useModal(rest);
  const { create } = useModalActions();
  const { t } = useTranslation();

  const [requiresRoikCode, setRequiresRoikCode] = useState(false);
  const [
    requiresExistingAgreementOwnerLoginData,
    setRequiresExistingAgreementOwnerLoginData,
  ] = useState(false);

  const { data: registrationInfo } = useServiceProviderRegistrationInfoQuery({
    jointBillServiceId,
    serviceProviderId,
  });

  const schema = useAddBillSettingsModalSchema(
    registrationInfo,
    requiresRoikCode,
    requiresExistingAgreementOwnerLoginData,
  );

  const { handleSubmit, control, formState, setValue, watch } =
    useForm<TAddBillSettingsModalSchema>({
      defaultValues: {
        ibanNumber: COUNTRY_CODE === CountryCode.PL ? "PL" : undefined,
      },
      resolver: zodResolver(schema),
    });

  const dueDay = watch("dueDay");
  const paymentCode = watch("paymentCode");

  const debouncedPaymentCode = useDebounce(paymentCode, 500);

  const { data: instructionCodes } =
    useServiceProviderPaymentInstructionCodesQuery({
      paymentCode: debouncedPaymentCode,
      serviceProviderId,
    });

  useEffect(() => {
    setRequiresRoikCode(
      instructionCodes?.serviceProviderPaymentInstructionCodes.some(
        (codes) => codes.isRoikCodeRequired,
      ) ?? false,
    );
  }, [instructionCodes]);

  const { mutateAsync: addServiceProvider } = useAddServiceProviderMutation();

  const { toastApiError } = useApiErrorUtils();

  const onSubmit: SubmitHandler<TAddBillSettingsModalSchema> = async (data) => {
    const response = await addServiceProvider({
      jointBillServiceId,
      serviceProviderId,
      isIbanRequired: registrationInfo?.isIbanRequired ?? false,
      ...data,
      ibanNumber: data.ibanNumber?.replaceAll(" ", ""),
    }).catch((error) => {
      if (isAxiosError(error)) {
        const errorData = getAxiosErrorData(error);

        const isHouseholdConflict = errorData.errors?.some(
          (err) => err.messageCode === "AgreementExistsInOtherHouseHold",
        );

        const isAuthenticationError = errorData.errors?.some(
          (err) => err.errorCode === "AuthenticationError",
        );

        if (isHouseholdConflict) {
          setRequiresExistingAgreementOwnerLoginData(true);
        } else if (isAuthenticationError) {
          toast.error(t("IncorrectLoginData"));
        } else {
          toastApiError(error);
        }
      }
    });

    if (!isNullOrUndefined(response)) {
      modal.close();
      toast.success(t("addBillSettingsModal.toast.success"));

      if (response?.planChanged) {
        create((modalProps) => (
          <PlanChangedModal
            {...modalProps}
            jointBillServiceId={jointBillServiceId}
            isPlanChangeOptional={false}
          />
        ));
      }
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: isBillVariantSelectorEnabled ? ReceiptIcon : PlusIcon,
      }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {t("common.form.buttons.continue")}
          </Button>
        </React.Fragment>
      }
      title={registrationInfo?.serviceProviderName}
      subtitle={registrationInfo?.informationText}
    >
      <form className="flex flex-col gap-4">
        <FormInput
          name="displayName"
          control={control}
          label={t("addBillSettingsModal.fields.displayName")}
          placeholder={registrationInfo?.serviceProviderName}
        />
        {registrationInfo?.customerCodeRequired && (
          <FormInput
            name="customerCode"
            control={control}
            label={t("addBillSettingsModal.fields.customerCode")}
          />
        )}
        {registrationInfo?.requiresPaymentCode && (
          <div className="flex flex-col gap-1.5">
            <FormInput
              name="paymentCode"
              control={control}
              label={t("addBillSettingsModal.fields.paymentCode")}
            />
            {instructionCodes?.serviceProviderPaymentInstructionCodes.map(
              (code) => (
                <Label className="cursor-auto text-xs">
                  {code.description}
                </Label>
              ),
            )}
          </div>
        )}
        {requiresExistingAgreementOwnerLoginData && (
          <div className="flex flex-col gap-4">
            <span className="text-sm text-grey-dark">
              {t(
                "addBillSettingsModal.requiresExistingAgreementOwnerLoginData.title",
              )}
            </span>
            <FormInput
              name="existingAgreementOwnerLoginData.username"
              control={control}
              label={t("addBillSettingsModal.fields.username")}
            />
            <FormInput
              name="existingAgreementOwnerLoginData.password"
              type="password"
              control={control}
              label={t("addBillSettingsModal.fields.password")}
            />
          </div>
        )}
        {requiresRoikCode && (
          <FormInput
            name="roikCode"
            control={control}
            label={t("addBillSettingsModal.fields.roikCode")}
          />
        )}
        {registrationInfo?.requiresPersonalCode && (
          <FormInput
            name="personalCode"
            control={control}
            label={t("addBillSettingsModal.fields.personalCode")}
          />
        )}
        {registrationInfo?.requiresNameSurname && (
          <FormInput
            name="fullName"
            control={control}
            label={t("addBillSettingsModal.fields.fullName")}
          />
        )}
        {registrationInfo?.requiresPaymentPurpose && (
          <FormInput
            name="paymentPurpose"
            control={control}
            label={t("addBillSettingsModal.fields.paymentPurpose")}
          />
        )}
        {registrationInfo?.isIbanRequired && (
          <FormInput
            name="ibanNumber"
            control={control}
            label={t("addBillSettingsModal.fields.ibanNumber")}
          />
        )}
        <FeatureFlag flag={["isDueDateEnabled"]}>
          <DueDayPicker
            value={dueDay}
            onChange={(val) => {
              setValue("dueDay", val);
            }}
          />
        </FeatureFlag>
      </form>
    </Modal>
  );
};
